<template>
  <div class="shot-editing">
    <div class="layout-container">
      <div class="template-panel">
        <div class="template-tabs">
          <a-button
            :type="activeTab === 'store' ? 'primary' : 'default'"
            class="tab-btn"
            @click="switchTab('store')"
          >
            实体店
          </a-button>
          <a-button
            :type="activeTab === 'factory' ? 'primary' : 'default'"
            class="tab-btn"
            @click="switchTab('factory')"
          >
            工厂或企业
          </a-button>
          <a-button
            :type="activeTab === 'ecommerce' ? 'primary' : 'default'"
            class="tab-btn"
            @click="switchTab('ecommerce')"
          >
            带货
          </a-button>
          <a-button
            :type="activeTab === 'custom' ? 'primary' : 'default'"
            class="tab-btn"
            @click="switchTab('custom')"
          >
            自定义模板
          </a-button>
        </div>

        <div class="template-count">共 {{ currentTemplates.length }} 个模板</div>

        <div class="template-list">
          <div
            v-if="currentTemplates.length === 0 && activeTab !== 'custom'"
            class="template-empty"
          >
            暂无模板，请选择其他分类
          </div>
          <div
            v-if="activeTab === 'custom' && customTemplates.length === 0"
            class="template-empty"
          >
            暂无自定义模板，请点击下方按钮新建
          </div>
          <div
            class="template-item"
            v-for="tpl in currentTemplates"
            :key="tpl.name"
            :class="{ active: selectedTemplate === tpl.name }"
            @click="selectTemplate(tpl)"
          >
            <div class="template-item-header">
              <span class="template-item-name">{{ tpl.name }}</span>
              <span class="template-item-tag">{{ tpl.tag }}</span>
              <a-button
                v-if="activeTab === 'custom'"
                type="text"
                danger
                size="small"
                class="btn-delete-template"
                @click.stop="deleteCustomTemplate(tpl.name)"
              >
                <delete-outlined />
              </a-button>
            </div>
            <div class="template-item-desc">共 {{ tpl.shots ? (typeof tpl.shots === 'string' ? JSON.parse(tpl.shots).length : tpl.shots.length) : tpl.shotCount }} 个镜头</div>
          </div>
        </div>

        <div v-if="activeTab === 'custom'" class="custom-template-actions">
          <a-button type="dashed" block @click="showNewTemplateModal">
            <plus-outlined /> 新建模板
          </a-button>
          <a-button
            v-if="activeTab === 'custom' && selectedTemplate"
            type="primary"
            block
            class="btn-save-template"
            @click="saveCurrentTemplate"
          >
            <save-outlined /> 保存当前模板
          </a-button>
        </div>

        <a-modal
          v-model:open="newTemplateModalVisible"
          title="新建自定义模板"
          ok-text="确定"
          cancel-text="取消"
          @ok="confirmNewTemplate"
        >
          <a-input
            v-model:value="newTemplateName"
            placeholder="请输入模板名称"
            class="custom-template-name-input"
          />
        </a-modal>
      </div>

      <div class="editing-panel">
        <div v-if="shots.length === 0" class="no-template-hint">
          请先在左侧选择一个模板
        </div>

        <div class="shot-list" v-else>
          <div class="shot-card" v-for="(shot, index) in shots" :key="shot.shot_order">
            <div class="shot-header">
              <div class="shot-label">镜头{{ shot.shot_order }}</div>
              <a-button
                type="text"
                danger
                size="small"
                class="btn-delete-shot"
                @click="removeShot(index)"
              >
                <delete-outlined /> 删除
              </a-button>
            </div>
            <div class="shot-textarea">
              <span class="field-label">镜头文案</span>
              <a-textarea
                v-model:value="shot.shot_text"
                :rows="3"
                class="field-textarea"
                placeholder="请输入镜头文案"
                @change="onShotTextChange"
              />
            </div>
            <div class="shot-material">
              <div class="material-row">
                <span class="category-label">素材文件夹</span>
                <a-select
                  v-model:value="shot.folder_key"
                  class="category-select"
                  placeholder="选择素材文件夹"
                  @change="onCategoryChange"
                >
                  <a-select-option
                    v-for="folder in materialFolders"
                    :key="folder.name"
                    :value="folder.name"
                  >
                    {{ folder.name }} ({{ folder.clipCount }}个片段)
                  </a-select-option>
                </a-select>
              </div>
              <div class="material-row">
                <span class="category-label">保留原声</span>
                <a-switch
                  v-model:checked="shot.keepOriginalAudio"
                  @change="onKeepAudioChange"
                />
                <span class="audio-hint">{{ shot.keepOriginalAudio ? '保留' : '消除' }}</span>
              </div>
              <div class="material-row">
                <span class="category-label">录音素材</span>
                <a-select
                  v-model:value="shot.audios"
                  class="audio-select"
                  placeholder="选择录音素材"
                  allow-clear
                >
                  <a-select-option
                    v-for="audio in audioFiles"
                    :key="audio.fileKey"
                    :value="audio.fileKey"
                  >
                    {{ audio.fileKey }}
                  </a-select-option>
                </a-select>
              </div>
            </div>
          </div>

          <div class="add-shot-wrap">
            <a-button type="dashed" class="btn-add-shot" @click="addShot">
              <plus-outlined /> 新增镜头
            </a-button>
          </div>
        </div>

        <div class="editing-footer">
          <div class="footer-left">
            <div class="footer-item">
              <span class="footer-label">背景音乐</span>
              <a-select
                v-model:value="selectedBgm"
                class="bgm-select"
                placeholder="选择背景音乐"
                allow-clear
                @change="onBgmChange"
              >
                <a-select-option
                  v-for="audio in audioFiles"
                  :key="audio.fileKey"
                  :value="audio.fileKey"
                >
                  {{ audio.fileKey }}
                </a-select-option>
              </a-select>
            </div>
            <div class="footer-item">
              <span class="footer-label">视频封面</span>
              <a-select
                v-model:value="selectedCover"
                class="cover-select"
                placeholder="选择视频封面"
                allow-clear
                @change="onCoverChange"
              >
                <a-select-option
                  v-for="image in imageFiles"
                  :key="image.fileKey"
                  :value="image.fileKey"
                >
                  {{ image.fileKey }}
                </a-select-option>
              </a-select>
            </div>
          </div>
          <a-button type="primary" class="btn-create-task" :loading="creating" :disabled="shots.length === 0" @click="handleCreateTask">创建任务</a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons-vue';
import {
  getTemplateList,
  saveTemplate,
  deleteTemplate,
  createTask
} from '@/api/videoEdit';

const MATERIAL_KEY = 'material_center_state';

function generateShots(templateName, count) {
  const shots = [];
  for (let i = 1; i <= count; i++) {
    shots.push({
      shot_order: i,
      shot_text: `${templateName}模板镜头${i}的文案内容`,
      folder_key: '',
      keepOriginalAudio: true,
      audios: '',
    });
  }
  return shots;
}

const builtInTemplates = {
  store: [
    { name: '家政', tag: '实体店', shotCount: 9, shots: generateShots('家政', 9) },
    { name: '旅游', tag: '实体店', shotCount: 10, shots: generateShots('旅游', 10) },
    { name: '服装', tag: '实体店', shotCount: 9, shots: generateShots('服装', 9) },
    { name: '生鲜批发', tag: '实体店', shotCount: 9, shots: generateShots('生鲜批发', 9) },
    { name: '美发店', tag: '实体店', shotCount: 9, shots: generateShots('美发店', 9) },
    { name: '美容院', tag: '实体店', shotCount: 8, shots: generateShots('美容院', 8) },
    { name: '装修', tag: '实体店', shotCount: 9, shots: generateShots('装修', 9) },
    { name: '金店', tag: '实体店', shotCount: 9, shots: generateShots('金店', 9) },
  ],
  factory: [
    { name: '机械制造', tag: '工厂或企业', shotCount: 8, shots: generateShots('机械制造', 8) },
    { name: '电子产品', tag: '工厂或企业', shotCount: 7, shots: generateShots('电子产品', 7) },
  ],
  ecommerce: [
    { name: '食品带货', tag: '带货', shotCount: 6, shots: generateShots('食品带货', 6) },
    { name: '服装带货', tag: '带货', shotCount: 7, shots: generateShots('服装带货', 7) },
  ],
};

const customTemplates = ref([]);

const templates = computed(() => ({
  ...builtInTemplates,
  custom: customTemplates.value,
}));

const currentTemplates = computed(() => templates.value[activeTab.value] || []);

const activeTab = ref('store');
const selectedTemplate = ref('');
const creating = ref(false);

const materialFolders = ref([]);
const audioFiles = ref([]);
const imageFiles = ref([]);
const selectedBgm = ref('');
const selectedCover = ref('');
const newTemplateName = ref('');
const newTemplateModalVisible = ref(false);

const shots = ref([]);

onMounted(() => {
  loadCustomTemplates();
  loadMaterialFolders();
});

async function loadCustomTemplates() {
  try {
    const list = await getTemplateList();
    console.log('Loaded templates:', list);
    customTemplates.value = list.map(t => {
      console.log('Template item:', t);
      return {
        id: t.id,
        name: t.templateName || t.template_name || '未命名',
        tag: '自定义',
        shotCount: t.shotCount || t.shot_count || 0,
        shots: typeof t.shots === 'string' ? JSON.parse(t.shots) : t.shots,
      };
    });
    console.log('Mapped templates:', customTemplates.value);
  } catch (e) {
    console.warn('Failed to load custom templates:', e);
  }
}

function showNewTemplateModal() {
  newTemplateName.value = '';
  newTemplateModalVisible.value = true;
}

async function confirmNewTemplate() {
  const name = newTemplateName.value.trim();
  if (!name) {
    message.warning('请输入模板名称');
    return;
  }
  if (customTemplates.value.some(t => t.name === name)) {
    message.warning('模板名称已存在');
    return;
  }
  const newTemplate = {
    name,
    tag: '自定义',
    shotCount: 1,
    shots: [{ shot_order: 1, shot_text: '', folder_key: '', keepOriginalAudio: true, audios: '' }],
  };
  try {
    await saveTemplate({
      templateName: name,
      shotCount: 1,
      shots: JSON.stringify(newTemplate.shots),
    });
    customTemplates.value.push(newTemplate);
    newTemplateModalVisible.value = false;
    message.success('模板创建成功');
  } catch (e) {
    message.error('创建模板失败：' + e.message);
  }
}

async function deleteCustomTemplate(tplName) {
  if (!tplName) {
    message.warning('模板名称为空');
    return;
  }
  try {
    console.log('Deleting template:', tplName);
    await deleteTemplate(tplName);
    const idx = customTemplates.value.findIndex(t => t.name === tplName);
    if (idx !== -1) {
      customTemplates.value.splice(idx, 1);
    }
    if (selectedTemplate.value === tplName) {
      selectedTemplate.value = '';
      shots.value = [];
    }
    message.success('模板已删除');
  } catch (e) {
    console.error('Delete template error:', e);
    message.error('删除模板失败：' + e.message);
  }
}

async function saveCurrentTemplate() {
  if (!selectedTemplate.value) {
    message.warning('请先选择一个模板');
    return;
  }
  const idx = customTemplates.value.findIndex(t => t.name === selectedTemplate.value);
  if (idx !== -1) {
    customTemplates.value[idx].shotCount = shots.value.length;
    customTemplates.value[idx].shots = JSON.parse(JSON.stringify(shots.value));
    try {
      await saveTemplate({
        templateName: selectedTemplate.value,
        shotCount: shots.value.length,
        shots: JSON.stringify(shots.value),
      });
      message.success('模板已保存');
    } catch (e) {
      message.error('保存模板失败：' + e.message);
    }
  }
}

function loadMaterialFolders() {
  try {
    const saved = localStorage.getItem(MATERIAL_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      if (state.folders) {
        materialFolders.value = state.folders.map(f => ({
          name: f.name,
          clipCount: f.clips ? f.clips.length : 0,
          clips: f.clips || [],
        }));
        const allClips = state.folders.flatMap(f => f.clips || []);
        audioFiles.value = allClips
          .filter(c => c.fileType === 'audio' && c.fileKey)
          .map(c => ({ fileKey: c.fileKey }));
        imageFiles.value = allClips
          .filter(c => c.fileType === 'image' && c.fileKey)
          .map(c => ({ fileKey: c.fileKey }));
      }
    }
  } catch (e) {
    console.warn('Failed to load material folders:', e);
  }
}

function onBgmChange() {
  // BGM 选择变化，无需额外操作
}

function onCoverChange() {
  // 封面选择变化，无需额外操作
}

function switchTab(tab) {
  activeTab.value = tab;
  selectedTemplate.value = '';
  shots.value = [];
}

function selectTemplate(tpl) {
  selectedTemplate.value = tpl.name;
  shots.value = JSON.parse(JSON.stringify(tpl.shots));
}

function onCategoryChange() {
  // 分类变化时的处理
}

function onShotTextChange() {
  // 文案变化时的处理
}

function onKeepAudioChange() {
  // 保留原声设置变化时的处理
}

function addShot() {
  const maxOrder = shots.value.reduce((max, s) => Math.max(max, s.shot_order || 0), 0);
  const newShot = {
    shot_order: maxOrder + 1,
    shot_text: '',
    folder_key: '',
    keepOriginalAudio: true,
    audios: '',
  };
  shots.value.push(newShot);
  message.success('已新增镜头');
}

function removeShot(index) {
  shots.value.splice(index, 1);
  shots.value.forEach((shot, idx) => {
    shot.shot_order = idx + 1;
  });
  message.success('已删除镜头');
}

async function handleCreateTask() {
  if (shots.value.length === 0) {
    message.warning('请先选择一个模板');
    return;
  }

  creating.value = true;

  try {
    // 查找当前模板的 ID
    const currentTpl = customTemplates.value.find(t => t.name === selectedTemplate.value);
    const templateId = currentTpl ? currentTpl.id : null;

    // 生成时间戳 YYYYMMDDHHmmss
    const now = new Date();
    const timestamp = 
      now.getFullYear().toString() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0') +
      String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');

    const task = {
      templateId: templateId,
      taskName: `${selectedTemplate.value}-${timestamp}`,
      status: 'pending',
      shots: JSON.stringify(shots.value),
      bgmFileKey: selectedBgm.value,
      coverFileKey: selectedCover.value,
    };

    await createTask(task);
    message.success('任务创建成功，请到任务中心查看');
  } catch (e) {
    message.error('创建任务失败：' + e.message);
  } finally {
    creating.value = false;
  }
}
</script>

<style scoped>
.shot-editing {
  min-height: 100%;
}

.layout-container {
  display: flex;
  gap: 20px;
  min-height: calc(100vh - 120px);
}

.template-panel {
  width: 340px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
}

.template-tabs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.tab-btn {
  border-radius: 8px;
  font-size: 13px;
}

.template-count {
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 12px;
}

.template-empty {
  text-align: center;
  color: #bfbfbf;
  padding: 40px 20px;
  font-size: 14px;
}

.template-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.template-item {
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.template-item:hover {
  border-color: #4096ff;
  background: #f0f5ff;
}

.template-item.active {
  border-color: #4096ff;
  background: #f0f5ff;
}

.template-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.btn-delete-template {
  margin-left: auto;
  font-size: 12px;
}

.custom-template-actions {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-save-template {
  border-radius: 8px;
}

.template-item-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.template-item-tag {
  font-size: 11px;
  color: #fa8c16;
  background: #fff7e6;
  padding: 1px 6px;
  border-radius: 4px;
}

.template-item-desc {
  font-size: 12px;
  color: #8c8c8c;
}

.editing-panel {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
}

.no-template-hint {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bfbfbf;
  font-size: 15px;
}

.shot-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;
}

.shot-card {
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 16px;
}

.shot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.shot-label {
  display: inline-block;
  background: #4096ff;
  color: #fff;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 4px;
}

.btn-delete-shot {
  font-size: 12px;
}

.shot-textarea {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  font-size: 13px;
  color: #595959;
  margin-bottom: 6px;
}

.field-textarea {
  width: 100%;
  border-radius: 8px;
}

.shot-material {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px dashed #f0f0f0;
}

.material-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.category-label {
  font-size: 13px;
  color: #595959;
  min-width: 70px;
}

.category-select {
  width: 240px;
}

.audio-select {
  width: 240px;
}

.audio-hint {
  font-size: 12px;
  color: #595959;
  margin-left: 8px;
}

.add-shot-wrap {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.btn-add-shot {
  border-radius: 8px;
  width: 100%;
  max-width: 300px;
}

.bgm-select {
  width: 240px;
}

.cover-select {
  width: 240px;
}

.editing-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.footer-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.footer-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.footer-label {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  min-width: 70px;
}

.btn-create-task {
  border-radius: 8px;
  padding: 0 28px;
  height: 38px;
  font-size: 14px;
}
</style>
